import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { Box, Grid, Palette, Typography } from '@mui/material';
import { TextField } from '@components/textField/TextField';
import { AttachmentFile } from '@components/container/AttachmentFile';
import { formatBytes } from '@utils/fileUtils';
import { DragEvent, ChangeEvent } from 'react';
import { IconButton } from '@components/iconButton';
import { TrashAlt } from '@assets/svgs';
import FileDropWrapper from '@utils/FileDropWrapper';
import { Button } from '@components/button';
import { FILE_TYPES } from '@components/drawerItem/DrawerItem.constants';
import { PostArtifact } from '@models/note';

type UploadFilesViewProps = {
  files: PostArtifact[];
  invalidFiles: File[];
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleDragOver: (e: DragEvent) => void;
  handleDragLeave: (e: DragEvent) => void;
  handleDrop: (e: DragEvent) => void;
  maxFilesAmount: number;
  dragging: boolean;
};

const filesColumn: GridColDef[] = [
  {
    field: '1',
    headerName: 'File Name/Type',
    flex: 3,
    sortable: false,
    display: 'flex',
    renderCell: (params) => (
      <Box
        sx={{
          marginY: 1,
        }}
      >
        <AttachmentFile {...params.row} />
      </Box>
    ),
  },
  {
    field: '2',
    headerName: 'Size',
    flex: 1,
    sortable: false,
    display: 'flex',
    renderCell: (params: GridRenderCellParams) => (
      <Typography variant='bodySmall'>{formatBytes(params.row.size)}</Typography>
    ),
  },
  {
    field: '3',
    headerName: 'Description',
    flex: 2,
    sortable: false,
    display: 'flex',
    renderCell: (params) => {
      return params.row.invalidMsg ? (
        <Typography
          variant='bodySmall'
          sx={{
            color: 'error.main',
          }}
        >
          Sorry. This file cannot be attached.
        </Typography>
      ) : (
        <TextField
          variant='outlined'
          placeholder='Add a description for this file'
          value={params.row.description}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            params.row.handleDescriptionChange(e, params.row.index);
          }}
          fullWidth
          otherInputProps={{ inputProps: { maxLength: 80 } }}
        />
      );
    },
  },
  {
    field: '4',
    headerName: '',
    width: 50,
    sortable: false,
    display: 'flex',
    renderCell: (params: GridRenderCellParams) => (
      <IconButton
        data-testid='removeBtn'
        onClick={() => params.row.handleRemove(params.row.index)}
        sx={{
          color: 'icon.dark',
        }}
      >
        <TrashAlt />
      </IconButton>
    ),
  },
];

const getBackgroundColor = (dragging: boolean, palette: Palette) => {
  if (dragging) {
    if (palette.theme === 'lio') {
      return '#465D7733';
    }

    return `${palette.background.main}10`;
  }
  return 'background.light';
};

export const UploadFilesView = ({
  files,
  invalidFiles,
  handleChange,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  maxFilesAmount,
  dragging,
}: UploadFilesViewProps) => {
  const acceptedFileTypes = FILE_TYPES.map((el) => `.${el}`).join(', ');
  const maxFilesReached = files.length === maxFilesAmount && invalidFiles.length > 0;
  const getFileSize = () => files.reduce((partialSum, a) => partialSum + a.size, 0);

  const noFilesToDisplay = !files || !files?.length;
  return (
    <Grid
      size={{ xs: 11 }}
      sx={{
        m: 2,
      }}
    >
      <Box
        sx={{
          marginBottom: 2,
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Typography variant='h5'>{`${files.length} File(s) Attached`}</Typography>
        <Typography
          variant='bodySmall'
          data-testid='filesize'
          sx={{
            marginLeft: 2,
          }}
        >
          {formatBytes(getFileSize())}
        </Typography>
      </Box>
      <Grid
        container
        sx={{
          ...styles.dataGrid,
          backgroundColor: 'background.light',
        }}
      >
        {/* Header */}
        <Grid container size={{ xs: 12 }}>
          <Grid
            size={{ xs: 12 }}
            container
            sx={{
              margin: 0,
              paddingY: 0,
              paddingX: 1,
              borderBottom: `1px solid black`,
              borderBottomColor: 'other.divider',
            }}
          >
            {filesColumn?.map((column) => {
              return (
                <Grid
                  key={column.field}
                  sx={{
                    marginY: 0,
                    flex: column.flex,
                    width: column.width,
                  }}
                >
                  <Typography
                    variant='bodySmall'
                    className='MuiDataGrid-columnHeaderTitle'
                    sx={{
                      color: (theme) => `${theme.palette.text.main} !important`,
                    }}
                  >
                    {column.headerName}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>

          {/* Rows */}
          <Grid
            container
            size={{ xs: 12 }}
            sx={[
              {
                paddingX: 1,
                height: '120px',
                maxHeight: '120px',
                overflow: 'auto',
              },
              noFilesToDisplay ? { alignContent: 'center', justifyContent: 'center' } : {},
            ]}
          >
            {noFilesToDisplay ? (
              <Typography
                variant='bodySmall'
                sx={{
                  marginBottom: '16px',
                }}
              >
                No files to display
              </Typography>
            ) : null}

            {files?.map((file: any, index) => {
              const key = `${file?.name}-${file.lastModified}-${index}`;

              return (
                <Grid size={{ xs: 12 }} container key={key}>
                  {filesColumn?.map((column) => {
                    return (
                      <Grid
                        key={column.field}
                        sx={{
                          flex: column.flex,
                          width: column.width,
                        }}
                      >
                        <Box
                          sx={{
                            marginY: 1,
                          }}
                        >
                          {column.renderCell
                            ? column.renderCell({
                                row: file,
                              } as any)
                            : null}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FileDropWrapper
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            disabled={maxFilesReached}
          >
            <Grid
              container
              direction='column'
              sx={[
                {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                (theme) => ({
                  width: 'auto',
                  border: `1px dashed ${theme.palette.secondary.main}`,
                  margin: 1,
                  color: maxFilesReached ? 'text.disabled' : 'text.main',
                  backgroundColor: getBackgroundColor(dragging, theme.palette),
                }),
              ]}
            >
              <Typography variant='h5' sx={{ margin: 1 }}>
                Drag and Drop Attachments
              </Typography>
              <Typography
                variant='bodySmall'
                sx={{
                  textAlign: 'center',
                  color: maxFilesReached ? 'text.disabled' : 'text.main',
                  marginBottom: 1,
                }}
              >
                Drop .mp4, .png, .jpg, .gif, .docx, .xls, .pptx, .pdf, .msg, .csv or .txt files <br /> here or{' '}
                <Button
                  variant='text'
                  sx={{
                    padding: 0,
                    color: 'secondary.main',
                  }}
                  disabled={maxFilesReached}
                >
                  <label htmlFor='file-upload' style={{ cursor: 'pointer' }}>
                    <input
                      multiple
                      accept={acceptedFileTypes}
                      type='file'
                      title='file-upload'
                      id='file-upload'
                      style={{ display: 'none' }}
                      onChange={handleChange}
                      onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                        const element = event.target as HTMLInputElement;
                        element.value = '';
                      }}
                    />
                    Browse Your Files
                  </label>
                </Button>
              </Typography>
            </Grid>
          </FileDropWrapper>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 1,
            }}
          >
            <Typography
              variant='bodySmall'
              sx={{
                color: maxFilesReached ? 'error.main' : '',
              }}
            >
              {`Maximum of ${maxFilesAmount} file(s) allowed 
              ${maxFilesReached ? `- ${invalidFiles.map((file: File) => file.name).join(' ')} not added` : ''}
                `}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};
